import React, { useContext, useEffect, useState } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import MenuIcon from '@mui/icons-material/Menu'
import HistoryIcon from '@mui/icons-material/History'
import { GlobalContext, UserContext } from '../../../App.js'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame.jsx'
import useIsMobile from '../../../helpers/useIsMobile.js'
import SettingsMenu from './SettingsMenu.jsx'
import ConfigOverview from './ConfigOverview.jsx'
import ActivityLog from './ActivityLog/ActivityLog.jsx'
import Hospitals from './MedicalPlans/Hospitals.jsx'
import EMPTemplates from './MedicalPlans/EMPTemplates.jsx'
import { useConfigGroups, findConfigItem } from './configGroups.jsx'

// Load lists moved out of Site Configuration into the Make Ready module;
// keep old bookmarked editor links working.
const LoadListEditorRedirect = () => {
  const { loadListID } = useParams()
  return <Navigate to={`/make-ready-load-lists/${loadListID}`} replace />
}

const SiteConfiguration = () => {
  const { setToast } = useContext(GlobalContext)
  const { mainFrameContainerRef, sidebarState, setSidebarState } =
    useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  const { groups, isSiteAdmin } = useConfigGroups()

  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()

  const teams = user?.employee?.teams || []
  const hasAccess =
    isSiteAdmin ||
    teams.includes('Governance') ||
    teams.includes('Personnel') ||
    teams.includes('Fleet') ||
    teams.includes('Commercial Training') ||
    teams.includes('Event Planning') ||
    user?.employee?.ptsAccess?.includes('CoOrdinator')

  // The settings area has its own navigation, so tuck the main sidebar away
  useEffect(() => {
    let originalState = { ...sidebarState }
    setSidebarState({
      collapsed: true,
      mobileOpen: false,
    })

    return () => {
      setSidebarState(originalState)
    }
  }, [])

  useEffect(() => {
    if (mainFrameContainerRef.current) {
      mainFrameContainerRef.current.scrollTo({ top: 0, behaviour: 'smooth' })
    }
  }, [location])

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)

    const success = queryParams.get('success')
    if (success === 'googleCalendar') {
      setToast({ message: 'Google Calendar Authenticated' })
    }
  }, [location])

  // Phones start with the settings menu hidden (it's a temporary drawer, like
  // the main sidebar); desktop starts with it docked open. Re-sync when the
  // viewport crosses the breakpoint so rotating a tablet doesn't leave the
  // drawer stranded open.
  const [expandMenu, setExpandMenu] = useState(!isMobile)
  useEffect(() => {
    setExpandMenu(!isMobile)
  }, [isMobile])

  if (!hasAccess) {
    return (
      <Alert severity="error" variant="filled">
        You are not permitted to access this area.
      </Alert>
    )
  }

  const active = findConfigItem(groups, location.pathname)
  const onActivityLog = location.pathname.startsWith(
    '/site-configuration/activity-log',
  )

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => setExpandMenu((prev) => !prev)}
            aria-label="Toggle settings menu"
          >
            <MenuIcon />
          </IconButton>
          <Stack gap={0}>
            <Typography variant="title2">Site Configuration</Typography>
            <Typography variant="caption" color="text.secondary">
              How AmbuNet works for your organisation
            </Typography>
          </Stack>
        </Stack>
        {isSiteAdmin && !isMobile && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/site-configuration/activity-log')}
          >
            Activity Log
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          alignItems: 'flex-start',
        }}
        gap={1.5}
      >
        {isMobile ? (
          <Drawer
            anchor="left"
            open={!!expandMenu}
            onClose={() => setExpandMenu(false)}
          >
            <SettingsMenu
              groups={groups}
              isSiteAdmin={isSiteAdmin}
              isMobile={isMobile}
              setExpandMenu={setExpandMenu}
            />
          </Drawer>
        ) : (
          <Box
            sx={{
              width: expandMenu ? '16rem' : 0,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 300ms ease',
              position: 'sticky',
              top: 0,
            }}
          >
            <SettingsMenu groups={groups} isSiteAdmin={isSiteAdmin} />
          </Box>
        )}
        <Box
          // Never 0 on phones: pages are `Grid container spacing={2}` whose
          // -16px negative margin otherwise pulls the first row up into this
          // panel's clipping edge (sliced floating labels) and flush against
          // the screen. 12px absorbs the label overhang and keeps a margin.
          padding={isMobile ? 1.5 : 2.5}
          width="100%"
          // minWidth 0 lets this flex child shrink below its content width so
          // wide tables scroll in here instead of stretching the page
          sx={{
            minWidth: 0,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
          }}
          component={Paper}
          elevation={0}
        >
          {/* Contextual header so every subpage gets a consistent title
              without touching the forms themselves */}
          {(active || onActivityLog) && (
            <Stack gap={0.25} mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {onActivityLog ? 'Activity Log' : active.item.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {onActivityLog
                  ? 'Every recorded change to your site configuration.'
                  : active.item.description}
              </Typography>
            </Stack>
          )}
          <Routes>
            <Route
              path=""
              element={
                <ConfigOverview groups={groups} isSiteAdmin={isSiteAdmin} />
              }
            />
            {groups.map((group) =>
              group.items.map((item) => (
                <Route
                  key={item.slug}
                  path={item.slug}
                  element={item.component}
                />
              )),
            )}
            <Route path="medical-plans/hospitals" element={<Hospitals />} />
            <Route path="medical-plans/templates" element={<EMPTemplates />} />
            {/* Make Ready settings and load lists moved to the Make Ready
                module (/make-ready-load-lists); keep old links working. */}
            <Route
              path="make-ready"
              element={<Navigate to="/make-ready-load-lists" replace />}
            />
            <Route
              path="make-ready/load-lists"
              element={<Navigate to="/make-ready-load-lists" replace />}
            />
            <Route
              path="make-ready/setup"
              element={<Navigate to="/make-ready-load-lists/setup" replace />}
            />
            <Route
              path="make-ready/load-lists/new"
              element={<Navigate to="/make-ready-load-lists/new" replace />}
            />
            <Route
              path="make-ready/load-lists/:loadListID"
              element={<LoadListEditorRedirect />}
            />
            {/* Base locations moved to their own dashboard; keep old links working. */}
            <Route
              path="base-locations"
              element={<Navigate to="/base-locations" replace />}
            />
            <Route path="activity-log" element={<ActivityLog />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  )
}

export default SiteConfiguration
